import { useCallback, useEffect } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { RelationType } from '@/types'
import { morphToast } from '@/components/ui/Toast'
import { useDiagramStore, useSelectionStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'

export function useRelationCreation() {
  const { t } = useTranslation()
  const relMode = useSelectionStore((state) => state.relMode)

  useEffect(() => {
    if (!relMode) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        useSelectionStore.getState().cancelRelationshipCreation()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [relMode])

  const handleTableClick = useCallback((tableId: string, event: ReactMouseEvent) => {
    event.stopPropagation()

    const selection = useSelectionStore.getState()
    if (useDraftStore.getState().mode === 'view') {
      selection.selectTable(tableId)
      return
    }
    if (!selection.relMode) {
      selection.selectTable(tableId)
      return
    }

    if (!selection.relationshipSourceTableId) {
      selection.startRelationshipCreation(tableId, selection.relType)
      selection.selectTable(tableId)
      return
    }

    const previousRelationIds = new Set(useDiagramStore.getState().rels.map((relation) => relation.id))

    useDiagramStore.getState().createRelationship(
      selection.relationshipSourceTableId,
      tableId,
      selection.relType,
    )

    const newRelations = useDiagramStore
      .getState()
      .rels.filter((relation) => !previousRelationIds.has(relation.id))

    selection.cancelRelationshipCreation()

    if (newRelations.length === 1) {
      selection.selectRelationship(newRelations[0].id)
      return
    }

    selection.selectTable(tableId)
  }, [])

  const startRelationshipFromTable = useCallback((tableId: string, type?: RelationType) => {
    if (useDraftStore.getState().mode === 'view') return
    const selection = useSelectionStore.getState()
    selection.startRelationshipCreation(tableId, type ?? selection.relType)
    selection.selectTable(tableId)
    morphToast(t('relation.clickTarget'))
  }, [t])

  return {
    handleTableClick,
    startRelationshipFromTable,
  }
}
